import CallToAction from "components/CallToAction"
import cn from "classnames"
import Strikethrough from "public/assets/strike-through.svg"
import formatDollar from "utilities/formatDollar"

export default function PricingChartColumn(props) {
  const pricePerDay = formatDollar((Math.round((props.price / parseInt(props.item_quantity)) * 100) / 100).toFixed(2))

  const salesPricePerDay = formatDollar(
    (Math.round((props.sale_price / parseInt(props.item_quantity)) * 100) / 100).toFixed(2)
  )

  const percentageSavingsMessage =
    props.savings_percentage > 40
      ? `(UP TO ${props.savings_percentage}% SAVINGS)`
      : `(${props.savings_percentage}% SAVINGS)`

  switch (props.component) {
    case "pricing_daily_column":
      return <DailyColumn {...props} />
    case "pricing_package_column":
      return (
        <PackageColumn
          {...props}
          pricePerDay={pricePerDay}
          salesPricePerDay={salesPricePerDay}
          percentageSavingsMessage={percentageSavingsMessage}
        />
      )
    case "pricing_training_column":
      return <TrainingColumn {...props} />
    case "pricing_membership_column":
      return <MembershipColumn {...props} percentageSavingsMessage={percentageSavingsMessage} />
    default:
      return null
  }
}

const columnStyles = "grid grid-rows-[0.5fr,1fr,2fr,2fr] min-w-full px-16 md:px-48 lg:px-0 text-black"

const membershipColumnStyles = "grid grid-rows-[0.5fr,1fr,2fr,2fr,2fr] min-w-full px-16 md:px-48 lg:px-0 text-black"

function DailyColumn({
  highlight_toggle,
  highlight_message,
  heading,
  item_quantity,
  item_unit,
  price,
  discount_banner,
  sales_toggle,
  sale_price,
}) {
  return (
    <div className={columnStyles}>
      {highlight_toggle ? (
        <div className="bg-green-apple rounded-t-3xl w-full text-center -top-6 px-4 pt-2 text-s1 font-bold underline underline-offset-8 decoration-white decoration-2">
          {highlight_toggle && highlight_message ? highlight_message : "BEST VALUE!"}
        </div>
      ) : (
        <div className=""></div>
      )}
      <div
        className={cn({
          "flex flex-col items-center justify-center font-bold text-m1 p-6": true,
          "bg-green-sea": !highlight_toggle,
          "bg-green-apple": highlight_toggle,
        })}
      >
        {heading}
      </div>
      <div className="bg-white flex flex-col items-center justify-center border-b border-black">
        <div className="font-bold text-l2">{item_quantity}</div>
        <div>{item_unit}</div>
      </div>
      <div className="bg-white flex items-center justify-center">
        {sales_toggle ? (
          <div className="text-center flex flex-col items-center">
            <div className="font-bold text-m1 relative">
              {formatDollar(price)}
              <Strikethrough className="absolute top-1 -left-4" />
            </div>
            <div className="font-bold text-l2">{formatDollar(sale_price)}</div>
          </div>
        ) : (
          <div className="text-center">
            <div className="font-bold text-l2">{formatDollar(price)}</div>
          </div>
        )}
      </div>
      {discount_banner && (
        <div className="lg:hidden bg-green-sea font-bold text-center py-2 rounded-b-3xl text-black p-4 uppercase">
          {discount_banner}
        </div>
      )}
    </div>
  )
}

function PackageColumn({
  highlight_toggle,
  highlight_message,
  heading,
  item_quantity,
  item_unit,
  price,
  discount_banner,
  pricePerDay,
  salesPricePerDay,
  percentageSavingsMessage,
  sales_toggle,
  sale_price,
}) {
  return (
    <div className={columnStyles}>
      {highlight_toggle ? (
        <div className="bg-green-apple rounded-t-3xl w-full text-center -top-6 px-4 pt-2 text-s1 font-bold underline underline-offset-8 decoration-white decoration-2">
          {highlight_toggle && highlight_message ? highlight_message : "BEST VALUE!"}
        </div>
      ) : (
        <div className=""></div>
      )}
      <div
        className={cn({
          "flex flex-col items-center justify-center font-bold text-m1 p-6": true,
          "bg-green-sea": !highlight_toggle,
          "bg-green-apple": highlight_toggle,
        })}
      >
        {heading}
      </div>
      <div className="bg-white flex flex-col items-center justify-center border-b border-black">
        <div className="font-bold text-l2">{item_quantity}</div>
        <div>{item_unit}</div>
      </div>
      <div className="bg-white flex items-center justify-center">
        <div className="flex flex-col items-center text-center">
          {sales_toggle ? (
            <>
              <div className="font-bold text-m1 relative">
                {formatDollar(price)}
                <Strikethrough className="absolute top-1 -left-4" />
              </div>
              <div className="font-bold text-l2">{formatDollar(sale_price)}</div>
            </>
          ) : (
            <div className="font-bold text-l2">{formatDollar(price)}</div>
          )}
          <>
            {sales_toggle ? (
              <div className="text-s1">{salesPricePerDay}/day</div>
            ) : (
              <div className="text-s1">{pricePerDay}/day</div>
            )}
            <div className="text-s1">{percentageSavingsMessage}</div>
          </>
        </div>
      </div>
      {discount_banner && (
        <div className="lg:hidden bg-green-sea font-bold text-center py-2 rounded-b-3xl text-black p-4 uppercase">
          {discount_banner}
        </div>
      )}
    </div>
  )
}

function TrainingColumn({
  highlight_toggle,
  highlight_message,
  heading,
  item_quantity,
  item_unit,
  behavioral_price,
  obedience_price,
  discount_banner,
}) {
  return (
    <div className={columnStyles}>
      {highlight_toggle ? (
        <div className="bg-green-apple rounded-t-3xl w-full text-center -top-6 px-4 pt-2 text-s1 font-bold underline underline-offset-8 decoration-white decoration-2">
          {highlight_toggle && highlight_message ? highlight_message : "BEST VALUE!"}
        </div>
      ) : (
        <div className=""></div>
      )}
      <div
        className={cn({
          "flex flex-col items-center justify-center font-bold text-m1 p-6": true,
          "bg-green-sea": !highlight_toggle,
          "bg-green-apple": highlight_toggle,
        })}
      >
        {heading}
      </div>
      <div className="bg-white flex flex-col items-center justify-center border-b border-black">
        <div className="font-bold text-l2">{item_quantity}</div>
        <div>{item_unit}</div>
      </div>
      <div className="bg-white flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-s1 font-light uppercase">Obedience</h3>
          <div className="font-bold text-l2">{formatDollar(obedience_price)}</div>
          <h3 className="text-s1 font-light uppercase">Behavioral</h3>
          <div className="font-bold text-l2">{formatDollar(behavioral_price)}</div>
        </div>
      </div>
      {discount_banner && (
        <div className="lg:hidden bg-green-sea font-bold text-center py-2 rounded-b-3xl text-black p-4 uppercase">
          {discount_banner}
        </div>
      )}
    </div>
  )
}

function MembershipColumn({
  highlight_toggle,
  highlight_message,
  heading,
  item_quantity,
  item_unit,
  price,
  discount_banner,
  percentageSavingsMessage,
  membership_cta_text,
  sales_toggle,
  sale_price,
}) {
  return (
    <div className={membershipColumnStyles}>
      {highlight_toggle ? (
        <div className="bg-green-apple rounded-t-3xl w-full text-center -top-6 px-4 pt-2 text-s1 font-bold underline underline-offset-8 decoration-white decoration-2">
          {highlight_toggle && highlight_message ? highlight_message : "BEST VALUE!"}
        </div>
      ) : (
        <div className=""></div>
      )}
      <div
        className={cn({
          "flex flex-col items-center justify-center font-bold text-m1 p-6": true,
          "bg-green-sea": !highlight_toggle,
          "bg-green-apple": highlight_toggle,
        })}
      >
        {heading}
      </div>
      <div className="bg-white flex flex-col items-center justify-center border-b border-black">
        <div className="font-bold text-l2">{item_quantity}</div>
        <div>{item_unit}</div>
      </div>
      <div className="bg-white flex items-center justify-center border-b border-black">
        <div className="text-center flex flex-col items-center">
          {sales_toggle ? (
            <>
              <div className="font-bold text-m1 relative">
                {formatDollar(price)}
                <Strikethrough className="absolute top-1 -left-4" />
              </div>
              <div className="font-bold text-l2">{formatDollar(sale_price)}</div>
            </>
          ) : (
            <div className="font-bold text-l2">{formatDollar(price)}</div>
          )}
          <>
            <div className="text-s1">{percentageSavingsMessage}</div>
          </>
        </div>
      </div>
      <div className="bg-white flex flex-col items-center justify-center">
        {heading === "MONTHLY" ? (
          <CallToAction style="secondary" href={"#enroll-monthly"}>
            {membership_cta_text}
          </CallToAction>
        ) : (
          <CallToAction style="secondary" href={"#enroll-weekly"}>
            {membership_cta_text}
          </CallToAction>
        )}
      </div>
      {discount_banner && (
        <div className="lg:hidden bg-green-sea font-bold text-center py-2 rounded-b-3xl text-black p-4 uppercase">
          {discount_banner}
        </div>
      )}
    </div>
  )
}
